import { Link } from 'react-router-dom';
import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import { Repeater, emptyTranslatable } from '@/features/blocks/BlockFields.js';
import { dateOnly, price as formatPrice } from '@/lib/format.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { createContentApi, type ContentRecord } from '../content-api.js';

// ─── Paketlar ────────────────────────────────────────────────────────────────

export type DroneShowPackageRecord = ContentRecord & {
  title: TranslatableString;
  description: TranslatableString;
  droneCount: number;
  durationMinutes: number;
  priceFrom: number | null;
  features: TranslatableString[];
  image: MediaRef | null;
  isFeatured: boolean;
};

export const droneShowPackagesApi = createContentApi<DroneShowPackageRecord>('/droneshow/packages');

export function DroneShowPackagesListPage() {
  return (
    <ContentListPage<DroneShowPackageRecord>
      title="Dron-shou paketlari"
      collection="droneshow-packages"
      contentApi={droneShowPackagesApi}
      basePath="/droneshow/packages"
      createLabel="Paket qo‘shish"
      sortable
      emptyTitle="Hozircha paket yo‘q"
      emptyHint="Paketlar tartibi saytdagi jadval tartibini belgilaydi."
      actions={
        <Link to="/droneshow/projects">
          <Button variant="outline">Portfolio</Button>
        </Link>
      }
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'droneCount',
          header: 'Dronlar',
          align: 'right',
          width: '100px',
          render: (row) => <span className="text-n-600">{row.droneCount ?? 0}</span>,
        },
        {
          key: 'priceFrom',
          header: 'Narx',
          align: 'right',
          width: '170px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{formatPrice(row.priceFrom)}</span>,
        },
      ]}
    />
  );
}

type PackageForm = {
  title: TranslatableString;
  slug: string;
  description: TranslatableString;
  droneCount: string;
  durationMinutes: string;
  priceFrom: string;
  features: TranslatableString[];
  image: MediaRef | null;
  isFeatured: boolean;
};

export function DroneShowPackageEditPage() {
  return (
    <ContentEditPage<PackageForm>
      collection="droneshow-packages"
      contentApi={droneShowPackagesApi}
      basePath="/droneshow/packages"
      backLabel="Paketlar"
      newTitle="Yangi paket"
      previewable={false}
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: emptyTranslatable(),
        slug: '',
        description: emptyTranslatable(),
        droneCount: '0',
        durationMinutes: '0',
        priceFrom: '',
        features: [],
        image: null,
        isFeatured: false,
      })}
      fromRecord={(record) => {
        const pkg = record as DroneShowPackageRecord;
        return {
          title: pkg.title ?? emptyTranslatable(),
          slug: pkg.slug ?? '',
          description: pkg.description ?? emptyTranslatable(),
          droneCount: String(pkg.droneCount ?? 0),
          durationMinutes: String(pkg.durationMinutes ?? 0),
          priceFrom: pkg.priceFrom === null ? '' : String(pkg.priceFrom ?? ''),
          features: pkg.features ?? [],
          image: pkg.image ?? null,
          isFeatured: pkg.isFeatured ?? false,
        };
      }}
      toInput={(form) => ({
        title: form.title,
        slug: form.slug,
        description: form.description,
        droneCount: Number(form.droneCount) || 0,
        durationMinutes: Number(form.durationMinutes) || 0,
        priceFrom: form.priceFrom === '' ? null : Number(form.priceFrom),
        features: form.features,
        image: form.image,
        isFeatured: form.isFeatured,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Paket" />
            <CardBody className="space-y-4">
              <TranslatableInput
                label="Nomi"
                required
                value={form.title}
                onChange={(title) => setForm((current) => ({ ...current, title }))}
                error={fieldErrors['title.uz']}
              />
              <TranslatableInput
                label="Tavsif"
                multiline
                rows={3}
                value={form.description}
                onChange={(description) => setForm((current) => ({ ...current, description }))}
              />
              <MediaPicker
                label="Rasm"
                value={form.image}
                onChange={(image) => setForm((current) => ({ ...current, image }))}
              />
              <Repeater
                label="Nima kiradi"
                max={20}
                addLabel="Band qo‘shish"
                items={form.features}
                getId={(_, index) => `feature-${index}`}
                summary={(item, index) => item.uz || `${index + 1}-band`}
                create={() => emptyTranslatable()}
                onChange={(features) => setForm((current) => ({ ...current, features }))}
                renderItem={(item, update) => (
                  <TranslatableInput label="Band" value={item} onChange={update} />
                )}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Ko‘rsatkichlar" />
            <CardBody className="space-y-4">
              <Input
                type="number"
                label="Dronlar soni"
                min={0}
                value={form.droneCount}
                onChange={(event) =>
                  setForm((current) => ({ ...current, droneCount: event.target.value }))
                }
              />
              <Input
                type="number"
                label="Davomiyligi (daqiqa)"
                min={0}
                value={form.durationMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, durationMinutes: event.target.value }))
                }
              />
              <Input
                type="number"
                label="Narx (so‘mdan boshlab)"
                min={0}
                value={form.priceFrom}
                placeholder="Bo‘sh — «Narxni so‘rang»"
                onChange={(event) =>
                  setForm((current) => ({ ...current, priceFrom: event.target.value }))
                }
              />
              <Checkbox
                label="Ajratib ko‘rsatilsin"
                hint="«Ommabop tanlov» sifatida belgilanadi"
                checked={form.isFeatured}
                onChange={(event) =>
                  setForm((current) => ({ ...current, isFeatured: event.target.checked }))
                }
              />
              <Input
                label="Manzil (slug)"
                value={form.slug}
                onChange={(event) =>
                  setForm((current) => ({ ...current, slug: event.target.value }))
                }
                error={fieldErrors.slug}
              />
            </CardBody>
          </Card>
        </div>
      )}
    />
  );
}

// ─── Portfolio ───────────────────────────────────────────────────────────────

export type DroneShowProjectRecord = ContentRecord & {
  title: TranslatableString;
  description: TranslatableString;
  eventDate: string | null;
  city: TranslatableString;
  client: TranslatableString;
  droneCount: number;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  videoUrl: string | null;
  seo: SeoMeta;
};

export const droneShowProjectsApi = createContentApi<DroneShowProjectRecord>('/droneshow/projects');

export function DroneShowProjectsListPage() {
  return (
    <ContentListPage<DroneShowProjectRecord>
      title="Dron-shou portfolio"
      collection="droneshow-projects"
      contentApi={droneShowProjectsApi}
      basePath="/droneshow/projects"
      createLabel="Shou qo‘shish"
      emptyTitle="Hozircha shou qo‘shilmagan"
      actions={
        <Link to="/droneshow/packages">
          <Button variant="outline">Paketlar</Button>
        </Link>
      }
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'city',
          header: 'Shahar',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.city?.uz || '—'}</span>,
        },
        {
          key: 'eventDate',
          header: 'Sana',
          width: '120px',
          render: (row) => <span className="text-n-600">{dateOnly(row.eventDate)}</span>,
        },
      ]}
    />
  );
}

type ShowForm = {
  title: TranslatableString;
  slug: string;
  description: TranslatableString;
  eventDate: string;
  city: TranslatableString;
  client: TranslatableString;
  droneCount: string;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  videoUrl: string;
  seo: SeoMeta;
};

export function DroneShowProjectEditPage() {
  return (
    <ContentEditPage<ShowForm>
      collection="droneshow-projects"
      contentApi={droneShowProjectsApi}
      basePath="/droneshow/projects"
      backLabel="Portfolio"
      newTitle="Yangi shou"
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: emptyTranslatable(),
        slug: '',
        description: emptyTranslatable(),
        eventDate: '',
        city: emptyTranslatable(),
        client: emptyTranslatable(),
        droneCount: '0',
        coverImage: null,
        gallery: [],
        videoUrl: '',
        seo: emptySeo(),
      })}
      fromRecord={(record) => {
        const show = record as DroneShowProjectRecord;
        return {
          title: show.title ?? emptyTranslatable(),
          slug: show.slug ?? '',
          description: show.description ?? emptyTranslatable(),
          eventDate: show.eventDate ? String(show.eventDate).slice(0, 10) : '',
          city: show.city ?? emptyTranslatable(),
          client: show.client ?? emptyTranslatable(),
          droneCount: String(show.droneCount ?? 0),
          coverImage: show.coverImage ?? null,
          gallery: show.gallery ?? [],
          videoUrl: show.videoUrl ?? '',
          seo: show.seo ?? emptySeo(),
        };
      }}
      toInput={(form) => ({
        title: form.title,
        slug: form.slug,
        description: form.description,
        eventDate: form.eventDate || null,
        city: form.city,
        client: form.client,
        droneCount: Number(form.droneCount) || 0,
        coverImage: form.coverImage,
        gallery: form.gallery,
        videoUrl: form.videoUrl || null,
        seo: form.seo,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Shou" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Nomi"
                  required
                  value={form.title}
                  onChange={(title) => setForm((current) => ({ ...current, title }))}
                  error={fieldErrors['title.uz']}
                />
                <TranslatableInput
                  label="Tavsif"
                  multiline
                  rows={4}
                  value={form.description}
                  onChange={(description) => setForm((current) => ({ ...current, description }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Galereya" />
              <CardBody>
                <MediaGalleryPicker
                  label="Shou rasmlari"
                  value={form.gallery}
                  onChange={(gallery) => setForm((current) => ({ ...current, gallery }))}
                />
              </CardBody>
            </Card>

            <SeoPanel
              value={form.seo}
              onChange={(seo) => setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.title.uz || undefined}
            />
          </div>

          <Card>
            <CardHeader title="Ma’lumotlar" />
            <CardBody className="space-y-4">
              <MediaPicker
                label="Muqova rasmi"
                value={form.coverImage}
                onChange={(coverImage) => setForm((current) => ({ ...current, coverImage }))}
              />
              <Input
                type="date"
                label="O‘tkazilgan sana"
                value={form.eventDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, eventDate: event.target.value }))
                }
              />
              <TranslatableInput
                label="Shahar"
                value={form.city}
                onChange={(city) => setForm((current) => ({ ...current, city }))}
              />
              <TranslatableInput
                label="Buyurtmachi"
                value={form.client}
                onChange={(client) => setForm((current) => ({ ...current, client }))}
              />
              <Input
                type="number"
                label="Dronlar soni"
                min={0}
                value={form.droneCount}
                onChange={(event) =>
                  setForm((current) => ({ ...current, droneCount: event.target.value }))
                }
              />
              <Input
                label="Video havolasi"
                value={form.videoUrl}
                placeholder="https://www.youtube.com/watch?v=…"
                onChange={(event) =>
                  setForm((current) => ({ ...current, videoUrl: event.target.value }))
                }
              />
              <Input
                label="Manzil (slug)"
                value={form.slug}
                onChange={(event) =>
                  setForm((current) => ({ ...current, slug: event.target.value }))
                }
                error={fieldErrors.slug}
              />
            </CardBody>
          </Card>
        </div>
      )}
    />
  );
}
